import { useMutation } from '@tanstack/react-query';
import { useBunja } from 'bunja/react';
import { useStore } from 'jotai';

import { deleteAllTpslOrdersApi, deletePendingPositionApi, putOrderTpSlApi } from '@flipster/apis/v2/trade';
import { T } from '@flipster/i18n';
import { pendingPositionsBunja } from '@flipster/r230522/normal-sub/tables/private/orders';
import { ConditionalOrderTypeEnum } from '@flipster/r230522/original/order';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { getErrorMessage } from '@flipster/utils/error';

import { orderAttributionAtom } from '../../order-attribution';
import { buildRequestStamp, buildTradeRequestBase } from '../base-params';

/**
 * TP/SL 삭제 액션 (구 tpsl-modal/use-delete-tp-sl).
 * - deleteTpslQuery: position TP/SL은 주문 삭제(deletePendingPositionApi), order TP/SL은 필드 해제(putOrderTpSlApi)
 * - deleteAllTpslQuery: slot의 TP 또는 SL 전체 삭제 — 기존과 동일하게 attribution/accountId를 보내지 않는다
 * useMutation(훅) 형태 유지 — 소비자별 인스턴스로 per-instance 로딩 보존.
 */
export function useDeleteTpSl() {
  // orders/attribution은 렌더가 아니라 삭제 실행 시점에만 필요하다. reactive 구독하면 소켓 tick마다
  // 새 맵 객체가 와서 이 훅을 쓰는 컴포넌트(단일/복수 상태 버튼)가 매 tick 리렌더된다.
  // → 구독 대신 클릭(mutation) 시점에 store에서 fresh 값을 읽는다.
  const store = useStore();
  const { ordersMapAtom } = useBunja(pendingPositionsBunja);

  const deleteTpslQuery = useMutation({
    mutationFn: async ({
      symbol,
      orderId,
      tpslType,
      accountId,
    }: {
      symbol: string;
      orderId: string;
      tpslType: 'tp' | 'sl';
      accountId?: number;
    }) => {
      const attribution = store.get(orderAttributionAtom);
      const orders = store.get(ordersMapAtom);
      const openPositionOrderType = [ConditionalOrderTypeEnum.PositionSl, ConditionalOrderTypeEnum.PositionTp];
      const currentOrder = orders?.[symbol]?.find((o) => {
        return o.orderId === orderId;
      });

      if (!currentOrder) throw new Error('Tp/Sl Order not found');

      if (openPositionOrderType.includes(currentOrder.conditionalOrderType)) {
        await deletePendingPositionApi(symbol, orderId, {
          ...buildTradeRequestBase(attribution),
          accountId,
        });
      } else {
        await putOrderTpSlApi(symbol, orderId, {
          ...buildTradeRequestBase(attribution),
          type: tpslType === 'tp' ? 'OrderTp' : 'OrderSl',
          accountId,
        });
      }
    },
    onSuccess: () => {
      snackbar.success({ message: <T id='Position.r250401.Position_update_toast' /> });
    },
    onError: (error) => {
      snackbar.error({ message: getErrorMessage(error) });
    },
  });

  const deleteAllTpslQuery = useMutation({
    mutationFn: async ({ symbol, slot, tpslType }: { symbol: string; slot: number; tpslType: 'tp' | 'sl' }) => {
      return await deleteAllTpslOrdersApi({
        ...buildRequestStamp(),
        conditionalOrderType:
          tpslType === 'tp' ? ConditionalOrderTypeEnum.PositionTp : ConditionalOrderTypeEnum.PositionSl,
        symbol,
        slot,
      });
    },
    onSuccess: () => {
      snackbar.success({ message: <T id='Position.r250401.Position_update_toast' /> });
    },
    onError: (error) => {
      snackbar.error({ message: getErrorMessage(error) });
    },
  });

  return { deleteTpslQuery, deleteAllTpslQuery };
}
